import { useCallback, useEffect, useRef, useState } from 'react';
import { createCancelSource, fetchList, isCancel } from '../api';
import { PAGE_SIZE } from '../config';

/**
 * Paged creative list for one tab + one filter set.
 *
 * Correctness rules baked in here:
 *  - `filterKey` is a PRIMITIVE string (see `filterKeyOf` in config.js) so the
 *    effect deps never contain an object/array literal.
 *  - every fetch captures a monotonic request id; a response whose id is not
 *    the newest is dropped, so a slow page-1 can never clobber a fresh page-1.
 *  - the in-flight request is cancelled whenever the tab or filters change.
 *  - a mounted ref stops any setState after unmount.
 *  - `loadMore` early-returns while loading or when there is nothing more.
 */
const useCreativeList = ({ tab, filterKey }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  const sourceRef = useRef(null);

  const stateRef = useRef({ loading: true, loadingMore: false, hasMore: false, page: 0 });
  stateRef.current = { loading, loadingMore, hasMore, page };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (sourceRef.current) sourceRef.current.cancel('component unmounted');
    };
  }, []);

  const run = useCallback(
    async (nextPage, append) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (sourceRef.current) sourceRef.current.cancel('superseded by a newer request');
      const source = createCancelSource();
      sourceRef.current = source;

      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError(null);
      }

      let filters = {};
      try {
        filters = JSON.parse(filterKey);
      } catch (e) {
        filters = {};
      }

      try {
        const data = await fetchList(
          { tab, page: nextPage, limit: PAGE_SIZE, ...filters },
          source.token
        );
        if (!mountedRef.current || requestId !== requestIdRef.current) return;

        const list = Array.isArray(data?.data) ? data.data : [];
        setRows((prev) => (append ? [...prev, ...list] : list));
        setTotal(Number(data?.total) || 0);
        setHasMore(
          typeof data?.hasMore === 'boolean' ? data.hasMore : list.length >= PAGE_SIZE
        );
        setPage(nextPage);
        setError(null);
      } catch (err) {
        if (isCancel(err)) return;
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        setError(err?.message || 'Unable to load creatives.');
        if (!append) {
          setRows([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tab, filterKey]
  );

  useEffect(() => {
    setRows([]);
    setPage(0);
    setTotal(0);
    setHasMore(false);
    run(0, false);
    return () => {
      if (sourceRef.current) sourceRef.current.cancel('tab or filters changed');
    };
  }, [run]);

  const reload = useCallback(() => {
    run(0, false);
  }, [run]);

  const loadMore = useCallback(() => {
    const s = stateRef.current;
    if (s.loading || s.loadingMore || !s.hasMore) return;
    run(s.page + 1, true);
  }, [run]);

  /**
   * Merge a partial update into one row without refetching.
   * `patch` may be an object or `(row) => patchObject`.
   */
  const patchRow = useCallback((bscId, patch) => {
    if (!mountedRef.current) return;
    setRows((prev) =>
      prev.map((row) =>
        Number(row.bsc_id) === Number(bscId)
          ? { ...row, ...(typeof patch === 'function' ? patch(row) : patch) }
          : row
      )
    );
  }, []);

  /** Drop a row that no longer belongs in this tab. */
  const removeRow = useCallback((bscId) => {
    if (!mountedRef.current) return;
    setRows((prev) => prev.filter((row) => Number(row.bsc_id) !== Number(bscId)));
    setTotal((prev) => (prev > 0 ? prev - 1 : 0));
  }, []);

  return {
    rows,
    total,
    hasMore,
    page,
    loading,
    loadingMore,
    error,
    reload,
    loadMore,
    patchRow,
    removeRow,
  };
};

export default useCreativeList;
